import { useCallback, useEffect, useState } from "react";
import { TestFilterNote, TestRowBadge } from "./TestFilterNote";
import { OWNER_ROLE_REASONS, ownerRoleReasonKey } from "../../shared/owner-role";
import "./admin-owner-role.css";

// F128 master switch (Servis): the client's handover step 2 submit queues the
// `/setProOwner` run for the Mac Mini runner. While the switch is OFF (the
// shipped default) jobs still queue and the machine API hands nothing out, so
// the whole lane can be rehearsed before it is armed - the same contract F102
// uses one step earlier.
//
// The queue matters more here than on any sibling panel: this command is an
// ownership TRANSFER and the verdict is read from prose, so the instance's own
// answer rides every failed row. Turning the switch on is a decision made with
// those sentences on screen.

// The server's own vocabulary, never a hand-typed mirror; an unknown value
// renders raw rather than throwing the whole panel away on a missing key
// (`translate` THROWS - the F113 `mail.botTransferReason` bug).
const KNOWN_REASONS: readonly string[] = OWNER_ROLE_REASONS;

interface OwnerRoleJobRow {
  jobId: string;
  clientId: string;
  clientName: string;
  status: "pending" | "running" | "done" | "failed";
  attempts: number;
  reason: string | null;
  providerMessage: string | null;
  botUsername: string;
  createdAt: string;
  completedAt: string | null;
  clientFault: boolean;
  is_test: boolean;
}

interface OwnerRoleOverview {
  settings: { enabled: boolean };
  counts: { pending: number; running: number; done: number; failed: number };
  jobs: OwnerRoleJobRow[];
}

export function AdminOwnerRole(props: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  csrfToken: string;
  hideTestTenants: boolean;
  onHideTestTenantsChange: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
}) {
  const [overview, setOverview] = useState<OwnerRoleOverview | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "done" | "error">("idle");

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/owner-role", { credentials: "include" });
      if (!response.ok) {
        setLoadFailed(true);
        return;
      }
      setOverview(await response.json() as OwnerRoleOverview & { ok: true });
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveEnabled(enabled: boolean) {
    setSaving(true);
    setSaveState("idle");
    try {
      const response = await fetch("/api/admin/owner-role/settings", {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify({ enabled })
      });
      if (!response.ok) {
        setSaveState("error");
        return;
      }
      setSaveState("done");
      await load();
    } catch {
      setSaveState("error");
    } finally {
      setSaving(false);
    }
  }

  const jobs = overview?.jobs ?? [];
  const visibleJobs = props.hideTestTenants ? jobs.filter((job) => !job.is_test) : jobs;
  const hiddenJobs = jobs.length - visibleJobs.length;

  return (
    <div className="owner-role-admin">
      {loadFailed && <p className="message error-message" role="alert">{props.t("staffConsole.ownerRole.loadFailed")}</p>}
      {overview && (
        <>
          <label className="checkbox-row owner-role-switch">
            <input
              type="checkbox"
              checked={overview.settings.enabled}
              disabled={saving}
              onChange={(event) => void saveEnabled(event.target.checked)}
            />
            <span>{props.t("staffConsole.ownerRole.enable")}</span>
          </label>
          <p className="admin-tool-hint">{props.t("staffConsole.ownerRole.switchHint")}</p>
          {saveState === "done" && <p className="message success-message" role="status">{props.t("staffConsole.ownerRole.saved")}</p>}
          {saveState === "error" && <p className="message error-message" role="alert">{props.t("staffConsole.ownerRole.saveFailed")}</p>}
          <p className="owner-role-counts">
            {props.t("staffConsole.ownerRole.counts", {
              pending: overview.counts.pending,
              running: overview.counts.running,
              done: overview.counts.done,
              failed: overview.counts.failed
            })}
          </p>
          <TestFilterNote
            shown={visibleJobs.length}
            hidden={hiddenJobs}
            t={props.t}
            onShowTest={() => props.onHideTestTenantsChange(false)}
          />
          {visibleJobs.length === 0
            ? <p className="admin-tool-hint">{props.t("staffConsole.ownerRole.empty")}</p>
            : (
              <ul className="owner-role-job-list">
                {visibleJobs.map((job) => (
                  <li key={job.jobId} className="owner-role-job" data-job-status={job.status}>
                    <div className="owner-role-job-main">
                      <strong className="owner-role-job-client">{job.clientName || job.clientId}</strong>
                      {job.is_test && <TestRowBadge t={props.t} />}
                      <span className="owner-role-job-bot">@{job.botUsername}</span>
                    </div>
                    <div className="owner-role-job-meta">
                      <span className={`status-pill owner-role-pill-${job.status}`}>
                        {props.t(`staffConsole.ownerRole.status.${job.status}`)}
                      </span>
                      {job.status === "failed" && job.reason && (
                        <span className="owner-role-job-reason">
                          {KNOWN_REASONS.includes(job.reason) ? props.t(ownerRoleReasonKey(job.reason)) : job.reason}
                        </span>
                      )}
                      {props.onOpenClient && (
                        <button
                          className="text-button"
                          type="button"
                          onClick={() => props.onOpenClient?.(job.clientId)}
                        >{props.t("staffConsole.ownerRole.openClient")}</button>
                      )}
                    </div>
                    {/* The verdict was read from this sentence, so it is the
                        first thing a human needs when a run went wrong. */}
                    {job.status === "failed" && job.providerMessage && (
                      <p className="owner-role-job-quote">
                        <span className="owner-role-job-quote-label">{props.t("staffConsole.ownerRole.quote")}</span>
                        <q>{job.providerMessage}</q>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
        </>
      )}
    </div>
  );
}
